import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import menus from "../data/barbequeen-menus.json";
import menuCategories from "../data/menuCategories.json";
import SEO from "../components/SEO";
import { menuPageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { menuFaqs } from "../data/faqs";

/* ─── Category tags ────────────────────────────────────────────── */
const getCategoryTag = (item) => {
  const n = (item.name || "").toLowerCase();
  if (n.includes("briyani") || n.includes("biryani") || n.includes("mandi"))
    return "Biryani";
  if (
    n.includes("grill") ||
    n.includes("bbq") ||
    n.includes("tandoori") ||
    n.includes("platter")
  )
    return "Grill & BBQ";
  if (n.includes("chicken") && !n.includes("grill")) return "Chicken";
  if (n.includes("mutton")) return "Mutton";
  if (n.includes("sea food") || n.includes("seafood")) return "Seafood";
  if (n.includes("soup")) return "Soups";
  if (
    n.includes("vegetable") ||
    n.includes("dosai") ||
    n.includes("bread") ||
    n.includes("egg")
  )
    return "Veg & Sides";
  if (
    n.includes("chinese") ||
    n.includes("noodle") ||
    n.includes("fried rice") ||
    n.includes("kothu")
  )
    return "Chinese & More";
  if (n.includes("arabian") || n.includes("family bucket")) return "Specials";
  return "Other";
};

const CATEGORY_META = {
  All: { color: "#9b1c2e", bg: "#fdf0f2" },
  "Grill & BBQ": { color: "#b45309", bg: "#fffbeb" },
  Biryani: { color: "#9b1c2e", bg: "#fdf0f2" },
  Chicken: { color: "#7c3aed", bg: "#f5f3ff" },
  Mutton: { color: "#065f46", bg: "#ecfdf5" },
  Seafood: { color: "#0369a1", bg: "#eff6ff" },
  Soups: { color: "#92400e", bg: "#fff7ed" },
  "Veg & Sides": { color: "#166534", bg: "#f0fdf4" },
  "Chinese & More": { color: "#1e3a5f", bg: "#eff6ff" },
  Specials: { color: "#9b1c2e", bg: "#fdf0f2" },
};

/* ─── Menu Card ────────────────────────────────────────────────── */
const MenuCard = ({ item, index }) => {
  const tag = getCategoryTag(item);
  const meta = CATEGORY_META[tag] || CATEGORY_META["All"];
  // Find slug from menuCategories if available
  const catData = menuCategories.find((c) => String(c.id) === String(item.id));
  const detailPath = catData ? `/menu/${catData.slug}/${catData.id}` : `/menu`;

  return (
    <Link
      to={detailPath}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Image */}
      <div
        className="relative overflow-hidden"
        style={{ paddingBottom: "66%" }}
      >
        <img
          src={item.image}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          onError={(e) => {
            e.target.src =
              "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=600&auto=format&fit=crop";
          }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Category badge */}
        <div
          className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase"
          style={{ background: meta.color, color: "#fff" }}
        >
          {tag}
        </div>

        {/* Hover arrow */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex-1 flex flex-col">
        <h3
          className="text-[16px] font-bold text-gray-900 leading-tight mb-2 group-hover:text-[#9b1c2e] transition-colors duration-200"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {item.name}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 flex-1">
          {item.description}
        </p>

        {/* View button */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs font-bold tracking-widest uppercase text-[#9b1c2e] group-hover:gap-2 flex items-center gap-1.5 transition-all duration-200">
            View Menu
            <svg
              className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </span>
          <span className="text-xs text-gray-300 font-sans">#{item.id}</span>
        </div>
      </div>
    </Link>
  );
};

/* ─── Main Page ────────────────────────────────────────────────── */
const MenuListPage = () => {
  const [activeTag, setActiveTag] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return menus.filter((item) => {
      const matchTag =
        activeTag === "All" || getCategoryTag(item) === activeTag;
      const matchSearch =
        !search ||
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase());
      return matchTag && matchSearch;
    });
  }, [activeTag, search]);

  return (
    <div className="min-h-screen bg-[#f9f5f0]">
      <SEO
        title="Barbequeen Menu | Smoky BBQ Grills, Dum Biryani & Mandi"
        metaTitle="Barbequeen Menu | Smoky BBQ Grills, Dum Biryani & Mandi"
        description="Explore the complete Barbequeen menu featuring double marinated grill chicken, authentic dum biryani, Arabian mandi, tandoori specials, Chinese dishes & seafood."
        keywords="Barbequeen menu, BBQ grill chicken, dum biryani, Arabian mandi, tandoori specials, shawarma, family bucket biryani, Chinese food, seafood, Salem restaurant menu"
        robots="index, follow"
        canonical="https://barbequeen.com/menu"
        og={{
          type: "website",
          url: "https://barbequeen.com/menu",
          title: "Explore Our Menu | Barbequeen Restaurant",
          description: "From smoky grills and dum biryani to Arabian mandi and tandoori specials — discover every flavour Barbequeen has to offer.",
          image: "https://barbequeen.com/images/menu-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN"
        }}
        geo={{
          region: "IN-TN",
          placename: "Tamil Nadu, India"
        }}
        schema={menuPageSchema}
      />
      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "340px" }}
      >
        <img
          src="https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=1600&auto=format&fit=crop"
          alt="Our Menu"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">
            Explore Our Kitchen
          </p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Our Menu
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed mb-8">
            From smoky grills to aromatic biryanis — discover every flavour
            Barbequeen has to offer.
          </p>

          {/* Search Bar */}
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search menu categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-5 py-4 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#9b1c2e] focus:bg-white/15 transition-all duration-200 backdrop-blur-sm"
            />
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 hover:text-white transition-colors"
              >
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Grid ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-12">
        {filtered.length === 0 ? (
          <div className="text-center py-24 space-y-4">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <svg
                className="w-10 h-10 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-base">No menu categories found.</p>
            <button
              onClick={() => {
                setActiveTag("All");
                setSearch("");
              }}
              className="text-xs font-bold tracking-widest uppercase text-[#9b1c2e] hover:underline"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((item, i) => (
              <MenuCard key={item.id} item={item} index={i} />
            ))}
          </div>
        )}
      </div>

      <FAQSection faqs={menuFaqs} />
    </div>
  );
};

export default MenuListPage;
